import React from "react";
import styled from "styled-components";
import { colors } from "utils";
import { formatDuration } from "./durations";
import { BORDER, Card } from "./styles";

const Tiles = styled(Card)`
  display: grid;
  gap: 0;
  grid-template-columns: 1.4fr 1fr 1fr 1fr 1fr;
  padding: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Tile = styled.dl`
  border-left: 1px solid ${BORDER};
  margin: 0;
  padding-left: 16px;

  &:first-child {
    border-left: 0;
    padding-left: 0;
  }
`;

const Label = styled.dt`
  color: ${colors.milkGlass950};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

const Value = styled.dd`
  color: ${(p) => (p.$primary ? colors.nightlyWoods500 : "inherit")};
  font-size: ${(p) => (p.$primary ? "40px" : "24px")};
  font-weight: ${(p) => (p.$primary ? 700 : 500)};
  line-height: ${(p) => (p.$primary ? 1.1 : 1.2)};
  margin: ${(p) => (p.$primary ? "0" : "4px 0 0")};
`;

const Hint = styled.dd`
  color: ${colors.milkGlass950};
  font-size: 12px;
  margin: 2px 0 0;
`;

const countOf = (count, noun) => `${count} ${count === 1 ? noun : `${noun}s`}`;

const WORDINGS = {
  user_wait: {
    typical: "Typical wait",
    half: "Half of all reports are ready sooner.",
    within: "ready within",
    measured: (summary) => countOf(summary.ready_count, "report")
  },
  report_generation: {
    typical: "Typical generation",
    half: "Half of all reports are generated faster.",
    within: "generated within",
    measured: (summary, reports) => countOf(summary.measurement_count, reports === "first" ? "first report" : "report")
  }
};

const tilesFor = (wording, reports) => [
  {
    label: wording.typical,
    primary: true,
    value: (summary) => formatDuration(summary.median_seconds),
    hint: (summary) => `${wording.half} ${wording.measured(summary, reports)} measured.`
  },
  { label: "p90", value: (summary) => formatDuration(summary.p90_seconds), hint: () => `9 in 10 ${wording.within}` },
  { label: "p95", value: (summary) => formatDuration(summary.p95_seconds), hint: () => `19 in 20 ${wording.within}` },
  { label: "p99", value: (summary) => formatDuration(summary.p99_seconds), hint: () => `99 in 100 ${wording.within}` },
  {
    label: "Needs Attention",
    value: (summary) => String(summary.no_report_count + summary.late_count),
    hint: (summary) => `${summary.no_report_count} without a report, ${summary.late_count} late`
  }
];

export const WaitFigures = ({ summary, measurement, reports }) => (
  <Tiles aria-label="Wait figures">
    {tilesFor(WORDINGS[measurement], reports).map((tile) => (
      <Tile key={tile.label}>
        <Label>{tile.label}</Label>
        <Value $primary={tile.primary}>{tile.value(summary)}</Value>
        <Hint>{tile.hint(summary)}</Hint>
      </Tile>
    ))}
  </Tiles>
);
